import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ErrorState } from '@/components/common/ErrorState';
import { DataPagination } from '@/components/pagination';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import {
  useMyForwardActivity,
  useMyOpportunityDismissals,
  useMyWithdrawalActivity,
  type ForwardActivityRow,
  type ObservabilityListQuery,
  type OpportunityDismissalRow,
  type WithdrawalActivityRow,
} from '@/hooks/api/useObservability';
import { formatDateTime, formatNumber } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const DEFAULT_FILTERS: ObservabilityListQuery = { page: 1, pageSize: 25 };
type ReportingTab = 'recommendations' | 'dismissals' | 'withdrawals';
type SortDirection = 'asc' | 'desc';
type SortState = {
  tab: ReportingTab;
  key: string;
  direction: SortDirection;
} | null;

const REPORTING_TABS: { label: string; value: ReportingTab }[] = [
  { label: 'Recommendations', value: 'recommendations' },
  { label: 'Dismissals', value: 'dismissals' },
  { label: 'Withdrawals', value: 'withdrawals' },
];

export function ReportingPage() {
  const [filters, setFilters] = useState<ObservabilityListQuery>(DEFAULT_FILTERS);
  const [activeTab, setActiveTab] = useState<ReportingTab>('recommendations');
  const [sort, setSort] = useState<SortState>(null);
  const dismissals = useMyOpportunityDismissals(filters);
  const forwards = useMyForwardActivity(filters);
  const withdrawals = useMyWithdrawalActivity(filters);

  const setPage = (page: number) => setFilters((current) => ({ ...current, page }));
  const setPageSize = (pageSize: number) =>
    setFilters((current) => ({ ...current, page: 1, pageSize }));
  const setSearch = (search: string) =>
    setFilters((current) => ({ ...current, page: 1, search: search || undefined }));

  const isError = dismissals.isError || forwards.isError || withdrawals.isError;
  const sortedForwards = useMemo(
    () =>
      sortRows(forwards.data?.rows ?? [], sort?.tab === 'recommendations' ? sort : null, {
        forwardedAt: (row) => row.forwardedAt,
        advisor: (row) => row.advisorName ?? '',
        client: (row) => row.clientName ?? '',
        policy: (row) => row.policyNumber ?? '',
        existingCarrier: (row) => row.currentCarrier ?? '',
        existingProduct: (row) => row.currentProduct ?? '',
        products: (row) => row.productCount,
      }),
    [forwards.data?.rows, sort]
  );
  const sortedDismissals = useMemo(
    () =>
      sortRows(dismissals.data?.rows ?? [], sort?.tab === 'dismissals' ? sort : null, {
        dismissedAt: (row) => row.dismissedAt,
        client: (row) => row.clientName ?? '',
        policy: (row) => row.policyNumber ?? '',
        existingCarrier: (row) => row.carrierName ?? '',
        existingProduct: (row) => row.productName ?? '',
        duration: (row) => formatDuration(row.duration),
      }),
    [dismissals.data?.rows, sort]
  );
  const sortedWithdrawals = useMemo(
    () =>
      sortRows(withdrawals.data?.rows ?? [], sort?.tab === 'withdrawals' ? sort : null, {
        withdrawnAt: (row) => row.withdrawnAt,
        forwardedAt: (row) => row.forwardedAt,
        advisor: (row) => row.advisorName ?? '',
        client: (row) => row.clientName ?? '',
        policy: (row) => row.policyNumber ?? '',
        existingCarrier: (row) => row.currentCarrier ?? '',
        existingProduct: (row) => row.currentProduct ?? '',
        products: (row) => row.productCount,
      }),
    [withdrawals.data?.rows, sort]
  );

  const toggleSort = (tab: ReportingTab, key: string) => {
    setSort((current) =>
      current?.tab === tab && current.key === key
        ? { tab, key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { tab, key, direction: 'asc' }
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">My Reporting</h1>
        <div
          className="inline-flex h-7 items-center rounded-md p-0.5"
          style={{ backgroundColor: 'hsl(var(--muted))' }}
        >
          {REPORTING_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setActiveTab(tab.value);
                setPage(1);
              }}
              className={cn(
                'h-6 rounded px-2 text-xs font-medium transition-colors',
                activeTab === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              style={
                activeTab === tab.value ? { backgroundColor: 'hsl(var(--background))' } : undefined
              }
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-border/70 bg-surface-2" style={{ boxShadow: 'var(--shadow-1)' }}>
        <CardContent className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium">My activity filters</p>
            <p className="text-xs text-muted-foreground">
              Search sent recommendations, dismissals, withdrawals, advisors, policies, and
              products.
            </p>
          </div>
          <Input
            className="max-w-sm"
            placeholder="Search activity..."
            value={filters.search ?? ''}
            onChange={(event) => setSearch(event.target.value)}
          />
        </CardContent>
      </Card>

      {isError ? (
        <ErrorState
          message="Failed to load reporting activity"
          onRetry={() => {
            void dismissals.refetch();
            void forwards.refetch();
            void withdrawals.refetch();
          }}
        />
      ) : (
        <>
          {activeTab === 'recommendations' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Recommendations Sent</CardTitle>
                <CardDescription>Recommendation packages you sent to the platform.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-xs text-muted-foreground">
                      <tr>
                        <SortableHeader
                          label="Date"
                          tab="recommendations"
                          sortKey="forwardedAt"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Advisor"
                          tab="recommendations"
                          sortKey="advisor"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Client"
                          tab="recommendations"
                          sortKey="client"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Policy"
                          tab="recommendations"
                          sortKey="policy"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Existing Carrier"
                          tab="recommendations"
                          sortKey="existingCarrier"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Product"
                          tab="recommendations"
                          sortKey="existingProduct"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Products"
                          tab="recommendations"
                          sortKey="products"
                          sort={sort}
                          onSort={toggleSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedForwards.map((row) => (
                        <tr key={row.id} className="border-b last:border-0">
                          <td className="px-3 py-2 text-muted-foreground">
                            {formatDateTime(row.forwardedAt)}
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.advisorName ?? 'Unknown'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.clientName ?? '—'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <PolicyLink id={row.opportunityId} label={row.policyNumber ?? 'View'} />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.currentCarrier ?? '—'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.currentProduct ?? '—'}
                              className="max-w-[200px]"
                            />
                          </td>
                          <td className="px-3 py-2 font-tabular">
                            {formatNumber(row.productCount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ReportingPagination
                  page={forwards.data?.page ?? filters.page ?? 1}
                  pageSize={forwards.data?.pageSize ?? filters.pageSize ?? 25}
                  total={forwards.data?.total ?? 0}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </CardContent>
            </Card>
          )}

          {activeTab === 'dismissals' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Dismissals</CardTitle>
                <CardDescription>
                  Opportunities you dismissed and the selected duration.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-xs text-muted-foreground">
                      <tr>
                        <SortableHeader
                          label="Date"
                          tab="dismissals"
                          sortKey="dismissedAt"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Client"
                          tab="dismissals"
                          sortKey="client"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Policy"
                          tab="dismissals"
                          sortKey="policy"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Existing Carrier"
                          tab="dismissals"
                          sortKey="existingCarrier"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Product"
                          tab="dismissals"
                          sortKey="existingProduct"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Duration"
                          tab="dismissals"
                          sortKey="duration"
                          sort={sort}
                          onSort={toggleSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDismissals.map((row) => (
                        <tr key={row.id} className="border-b last:border-0">
                          <td className="px-3 py-2 text-muted-foreground">
                            {formatDateTime(row.dismissedAt)}
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.clientName ?? '—'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <PolicyLink id={row.opportunityId} label={row.policyNumber ?? 'View'} />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.carrierName ?? '—'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.productName ?? '—'}
                              className="max-w-[200px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="secondary">{formatDuration(row.duration)}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ReportingPagination
                  page={dismissals.data?.page ?? filters.page ?? 1}
                  pageSize={dismissals.data?.pageSize ?? filters.pageSize ?? 25}
                  total={dismissals.data?.total ?? 0}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </CardContent>
            </Card>
          )}

          {activeTab === 'withdrawals' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Withdrawals</CardTitle>
                <CardDescription>
                  Sent recommendations you withdrew from the advisor workflow.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-xs text-muted-foreground">
                      <tr>
                        <SortableHeader
                          label="Withdrawn"
                          tab="withdrawals"
                          sortKey="withdrawnAt"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Sent"
                          tab="withdrawals"
                          sortKey="forwardedAt"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Advisor"
                          tab="withdrawals"
                          sortKey="advisor"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Client"
                          tab="withdrawals"
                          sortKey="client"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Policy"
                          tab="withdrawals"
                          sortKey="policy"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Existing Carrier"
                          tab="withdrawals"
                          sortKey="existingCarrier"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Product"
                          tab="withdrawals"
                          sortKey="existingProduct"
                          sort={sort}
                          onSort={toggleSort}
                        />
                        <SortableHeader
                          label="Products"
                          tab="withdrawals"
                          sortKey="products"
                          sort={sort}
                          onSort={toggleSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWithdrawals.map((row) => (
                        <tr key={row.id} className="border-b last:border-0">
                          <td className="px-3 py-2 text-muted-foreground">
                            {formatDateTime(row.withdrawnAt)}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {formatDateTime(row.forwardedAt)}
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.advisorName ?? 'Unknown'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.clientName ?? '—'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <PolicyLink id={row.opportunityId} label={row.policyNumber ?? 'View'} />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.currentCarrier ?? '—'}
                              className="max-w-[180px]"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <TruncatedTooltip
                              text={row.currentProduct ?? '—'}
                              className="max-w-[200px]"
                            />
                          </td>
                          <td className="px-3 py-2 font-tabular">
                            {formatNumber(row.productCount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ReportingPagination
                  page={withdrawals.data?.page ?? filters.page ?? 1}
                  pageSize={withdrawals.data?.pageSize ?? filters.pageSize ?? 25}
                  total={withdrawals.data?.total ?? 0}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function ReportingPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  return (
    <DataPagination
      page={page}
      pageSize={pageSize}
      totalCount={total}
      totalPages={Math.max(1, Math.ceil(total / pageSize))}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
}

function SortableHeader({
  label,
  tab,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  tab: ReportingTab;
  sortKey: string;
  sort: SortState;
  onSort: (tab: ReportingTab, key: string) => void;
}) {
  const isActive = sort?.tab === tab && sort.key === sortKey;

  return (
    <th className="px-3 py-2 text-left font-medium">
      <button
        type="button"
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium transition-colors hover:text-foreground',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
        onClick={() => onSort(tab, sortKey)}
      >
        {label}
        <span className="w-2 text-[10px] leading-none">
          {isActive ? (sort.direction === 'asc' ? '↑' : '↓') : ''}
        </span>
      </button>
    </th>
  );
}

function PolicyLink({ id, label }: { id: string; label: string }) {
  return (
    <TruncatedTooltip text={label} className="max-w-[180px]">
      <Link
        className="font-medium text-primary underline-offset-2 hover:underline"
        to={`/opportunities/${id}`}
      >
        {label}
      </Link>
    </TruncatedTooltip>
  );
}

function formatDuration(duration: string) {
  if (duration === '3_months') return '3 months';
  if (duration === '6_months') return '6 months';
  if (duration === '1_year') return '1 year';
  if (duration === 'never') return 'Never';
  return duration;
}

type ReportingRow = ForwardActivityRow | OpportunityDismissalRow | WithdrawalActivityRow;
type SortAccessors<T extends ReportingRow> = Record<string, (row: T) => string | number | null>;

function sortRows<T extends ReportingRow>(
  rows: T[],
  sort: SortState,
  accessors: SortAccessors<T>
): T[] {
  if (!sort) return rows;

  const accessor = accessors[sort.key];
  if (!accessor) return rows;

  const multiplier = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => compareSortValues(accessor(a), accessor(b)) * multiplier);
}

function compareSortValues(a: string | number | null, b: string | number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  const aTime = typeof a === 'string' ? Date.parse(a) : Number.NaN;
  const bTime = typeof b === 'string' ? Date.parse(b) : Number.NaN;
  if (Number.isFinite(aTime) && Number.isFinite(bTime)) {
    return aTime - bTime;
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}
